import { Avatar, Chip, Link, Stack, Typography } from '@mui/material'
import { FC, useMemo } from 'react'
import { Divider, Icons, ItemImageArea } from 'utoniq-ui'

import {
  ItemInformationQuery,
  ItemType,
  Maybe,
  UserInfoFragment,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'

type ItemDescriptionProps = {
  hiddenSku?: boolean
  item: ItemInformationQuery['item']
  user: Maybe<UserInfoFragment>
  number?: number
  showRemaining?: boolean
  remainingNumber?: string | null
  isThoughTicket?: boolean
}

export const ItemDescription: FC<ItemDescriptionProps> = ({
  hiddenSku,
  item,
  number,
  user,
  showRemaining,
  remainingNumber,
  isThoughTicket,
}) => {
  const { t } = useTranslation('itemInformation')
  const { generatePath, routerPages } = useRouter()

  return (
    <Stack>
      <Stack
        sx={{ px: 6, mb: 6 }}
        direction="row"
        spacing={6}
        alignItems="center"
      >
        <ItemImageArea
          size="small"
          src={item?.thumbnail.front?.mediumUri ?? ''}
        />
        <Stack spacing={3} flex={1}>
          <Stack>
            <Typography variant="h6">{item?.title}</Typography>
            {item?.sku && !hiddenSku && (
              <Typography variant="body2">{item.sku.unitName}</Typography>
            )}
          </Stack>
          {user && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack
                component={Link}
                href={generatePath(routerPages.UserProfile, {
                  userId: user.id,
                })}
                direction="row"
                spacing={2}
                alignItems="center"
              >
                <Avatar
                  src={user.avatar?.smallUri ?? ''}
                  sx={{ width: 20, height: 20 }}
                />
                <Typography variant="subtitle2" sx={styleSets.lineCamp(1)}>
                  {user.displayName}
                </Typography>
              </Stack>
              <Divider orientation="vertical" flexItem />
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="overline" color="text.hint">
                  {t('possessionNumber')}
                </Typography>
                <Typography variant="subtitle2">{number ?? '∞'}</Typography>
              </Stack>
            </Stack>
          )}
          {isThoughTicket && (
            <Chip
              label={
                <Typography variant="overline" color="white">
                  {t('throughTicket', '通し券')}
                </Typography>
              }
              sx={{
                bgcolor: 'secondary.main',
                p: 1,
                height: 20,
                '.MuiChip-label': { px: 0 },
              }}
            />
          )}
        </Stack>
      </Stack>
      {!item?.hideSalesNumber && (
        <ItemAsset
          user={user}
          item={item}
          showRemaining={showRemaining}
          remainingNumber={remainingNumber}
        />
      )}
      <Stack sx={{ pt: 3 }}>
        <Typography variant="body2">
          {item?.description ?? item?.metadata?.ticket?.description}
        </Typography>
      </Stack>
    </Stack>
  )
}

/**
 *  アイテムのレアリティ・発行枚数・タイプを表示するコンポーネント
 */
type ItemAssetProps = {
  showRemaining?: boolean
  remainingNumber?: string | null
  item: ItemInformationQuery['item']
  user: ItemDescriptionProps['user']
}
export const ItemAsset = ({
  user,
  item,
  showRemaining,
  remainingNumber,
}: ItemAssetProps) => {
  const { t } = useTranslation('itemInformation')
  const { generatePath, routerPages } = useRouter()

  const itemIcon = useMemo(() => {
    switch (item?.type) {
      case ItemType.Audio:
        return <Icons.Audio fontSize="xSmall" />
      case ItemType.Video:
        return <Icons.Video fontSize="xSmall" />
      case ItemType.Magazine:
        return <Icons.Magazine fontSize="xSmall" />
      case ItemType.Merch:
        return <Icons.Merch fontSize="xSmall" />
      case ItemType.Wallpaper:
        return <Icons.Wallpaper fontSize="xSmall" />
      case ItemType.Ticket:
        return <Icons.Ticket fontSize="xSmall" />
      default:
        return null
    }
  }, [item?.type])

  return (
    <Stack direction="row" spacing={3} alignItems="center">
      {item?.rarity && (
        <>
          <Stack spacing={1}>
            <Typography variant="overline" color="text.hint">
              {t('rarity')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {item?.rarityIconImage?.smallUri && (
                <Avatar
                  variant="square"
                  src={item?.rarityIconImage.smallUri}
                  sx={{
                    height: 12,
                    width: 12,
                  }}
                />
              )}
              <Typography variant="subtitle2">
                {item?.rarity?.name ? t(item.rarity.name) : '-'}
              </Typography>
            </Stack>
          </Stack>
          <Divider orientation="vertical" flexItem />
        </>
      )}
      <Stack spacing={1}>
        <Typography variant="overline" color="text.hint">
          {t('remainingQuantityPerTotal')}
        </Typography>
        <Typography variant="subtitle2">
          {showRemaining
            ? `${remainingNumber}/`
            : `${
                item?.issuedNumber
                  ? item?.issuedNumber - (item?.itemObtainedCount ?? 0)
                  : '∞'
              }/`}
          {item?.issuedNumber ?? '∞'}
        </Typography>
      </Stack>
      <Divider orientation="vertical" flexItem />
      <Stack spacing={1}>
        <Typography variant="overline" color="text.hint">
          {t('type')}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {itemIcon}
          <Typography variant="subtitle2">{t(item?.type)}</Typography>
        </Stack>
      </Stack>
      {!!item?.purchasableUsers?.length && item.issuedArtist?.id === user?.id && (
        <>
          <Divider orientation="vertical" flexItem />
          <Stack spacing={1}>
            <Typography variant="overline" color="text.hint">
              {t('whoCanPurchase', '購入可能者')}
            </Typography>

            <Link
              variant="subtitle2"
              href={generatePath(routerPages.ConsoleItemInformationLimitUser, {
                itemId: item.id,
              })}
            >
              {t('specified', '指定あり')}
            </Link>
          </Stack>
        </>
      )}
    </Stack>
  )
}
